import {
  ParentRelationshipKey,
  ShopifyAssetKey,
  ShopifyBusinessEntityKey,
  ShopifyCartKey,
  ShopifyCartLineItemKey,
  ShopifyCheckoutLineItemKey,
  ShopifyCheckoutShippingRateKey,
  ShopifyCollectKey,
  ShopifyCompanyLocationCatalogKey,
  ShopifyCustomerPaymentMethodKey,
  ShopifyDiscountCustomerBuysCollectionKey,
  ShopifyDiscountCustomerBuysProductKey,
  ShopifyDiscountCustomerBuysProductVariantKey,
  ShopifyDiscountCustomerGetsCollectionKey,
  ShopifyDiscountCustomerGetsProductKey,
  ShopifyDiscountCustomerGetsProductVariantKey,
  ShopifyFulfillmentLineItemKey,
  ShopifyFulfillmentLineItemToOrderLineItemKey,
  ShopifyFulfillmentServiceKey,
  ShopifyInventoryLevelKey,
  ShopifyInventoryQuantityKey,
  ShopifyMarketCatalogKey,
  ShopifyPriceListPriceKey,
  ShopifyProductMediaKey,
  ShopifyProductVariantMediaKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionManualDiscountKey,
} from "./keys";

export const REMOTE_IDENTIFICATION_KEYS_FOR_MODEL = {
  [ShopifyAssetKey]: ["theme_id", "key"],
  [ShopifyCartKey]: ["token"],
  [ShopifyCartLineItemKey]: ["key", ParentRelationshipKey],
  [ShopifyCompanyLocationCatalogKey]: ["catalogId", "companyLocationId"],
  [ShopifyCheckoutLineItemKey]: ["key"],
  [ShopifyCheckoutShippingRateKey]: ["id"],
  [ShopifyCollectKey]: ["productId", "collectionId"],
  [ShopifyCustomerPaymentMethodKey]: ["admin_graphql_api_id"],
  [ShopifyDiscountCustomerBuysProductKey]: ["productId", "discountId"],
  [ShopifyDiscountCustomerGetsProductKey]: ["productId", "discountId"],
  [ShopifyDiscountCustomerGetsProductVariantKey]: ["productVariantId", "discountId"],
  [ShopifyDiscountCustomerBuysProductVariantKey]: ["productVariantId", "discountId"],
  [ShopifyDiscountCustomerGetsCollectionKey]: ["collectionId", "discountId"],
  [ShopifyDiscountCustomerBuysCollectionKey]: ["collectionId", "discountId"],
  [ShopifyFulfillmentLineItemKey]: [
    // the id of the fulfillment line item in the shopify payload is actually the *order line item's id*, not the fulfillment line item's id. shopify (unhelpfully) doesn't send the fulfillment line item id at all, so we assign one on our side. we assume each order line item can only be (partially) fulfilled once per fulfillment, so (order_line_item_id, fulfillment_id) uniquely addresses a fulfillment line item.
    // confusingly, the id here is not the gadget assigned id for the fulfillment line item record, but the order line item id in the incoming.
    ShopifyFulfillmentLineItemToOrderLineItemKey,
    ParentRelationshipKey,
  ],
  [ShopifyFulfillmentServiceKey]: ["id"],
  [ShopifyInventoryLevelKey]: ["inventory_item_id", "location_id"],
  [ShopifyInventoryQuantityKey]: ["locationId", "inventoryItemId", "name"],
  [ShopifyMarketCatalogKey]: ["catalogId", "marketId"],
  [ShopifyPriceListPriceKey]: ["variantId", "priceListId"],
  [ShopifyProductMediaKey]: ["product_id", "file_id"],
  [ShopifyProductVariantMediaKey]: ["product_variant_id", "file_id"],
  [ShopifySubscriptionLineKey]: ["id"],
  [ShopifySubscriptionManualDiscountKey]: ["id"],
  [ShopifyBusinessEntityKey]: ["id"],
} as const;

export const getIdentificationKeysForModel = (modelKey: string): string[] | null => {
  // NOTE: this is not version-aware, use shopifyModelMetadataForVersion instead
  const keys = REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[modelKey];
  return keys ? [...keys] : null;
};

export type ShopifyIdentifyByRemoteKeys = (typeof REMOTE_IDENTIFICATION_KEYS_FOR_MODEL)[keyof typeof REMOTE_IDENTIFICATION_KEYS_FOR_MODEL];

/**
 * @internal
 * Allowlist for values in REMOTE_IDENTIFICATION_KEYS_FOR_MODEL - please make sure that these are IDs.
 * ModelField assumes that these are IDs, if a non ID value is added here, then
 * users could disable indexing on the field which needs indexing - causing slow queries and possibly an outage for us.
 */
export const _allowedRemoteIdKeysForModelValues = [ParentRelationshipKey, ShopifyFulfillmentLineItemToOrderLineItemKey];
